import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { IconGift } from '../components/icons';
import {
  Chips,
  DataTable,
  Dialog,
  Empty,
  ErrorNote,
  FlashNote,
  Loading,
  Note,
  PageHead,
  Pager,
  Pill,
  ReasonDialog,
  RowCard,
  useDialogClose,
  useFlash,
} from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { day, REWARD_KIND_LABEL, REWARD_STATUS_LABEL, REWARD_STATUS_TONE, rupees, shortId } from '../lib/format';
import { useMilestones } from '../lib/queries';
import { qk } from '../lib/realtime-cache';
import type { EarnedReward, Milestone, Page, RewardAudience, RewardKind } from '../lib/types';
import { rewardsRoute } from '../router';

/** "₹20 off a ride", "10% off (up to ₹50)", "₹1,000 bonus". */
export function rewardText(kind: RewardKind, paise: number, pct: number | null, maxPaise: number | null) {
  if (kind === 'RIDER_DISCOUNT_PERCENT') return `${pct ?? 0}% off a ride${maxPaise ? ` (up to ${rupees(maxPaise)})` : ''}`;
  if (kind === 'RIDER_RIDE_CREDIT') return `${rupees(paise)} off a ride`;
  return `${rupees(paise)} bonus`;
}

type StatusFilter = 'ALL' | EarnedReward['status'];

export function RewardsPage() {
  const { isOwner } = useAuth();
  const search = rewardsRoute.useSearch();
  const nav = rewardsRoute.useNavigate();
  const audience: RewardAudience = search.audience ?? 'RIDER';
  const page = search.page ?? 1;
  const qc = useQueryClient();
  const [flash, show] = useFlash();
  const [editing, setEditing] = useState<Milestone | 'new' | null>(null);
  const [voiding, setVoiding] = useState<EarnedReward | null>(null);
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [busy, setBusy] = useState<string | null>(null);

  const milestones = useMilestones();
  const mine = (milestones.data ?? []).filter((m) => m.audience === audience).sort((a, b) => a.threshold - b.threshold);
  const earned = useQuery({
    queryKey: [...qk.rewards, { audience, status, page }],
    queryFn: () => api.get<Page<EarnedReward>>('/admin/rewards', { audience, status: status === 'ALL' ? undefined : status, page, pageSize: 20 }),
    placeholderData: keepPreviousData,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.milestones });
    void qc.invalidateQueries({ queryKey: qk.rewards });
  };
  /** On / off shows at once (optimistic); the note offers Undo, which flips it back. */
  const setActive = async (m: Milestone, isActive: boolean, undoable = true) => {
    const before = qc.getQueryData<Milestone[]>(qk.milestones);
    qc.setQueryData<Milestone[]>(qk.milestones, (list) => list?.map((x) => (x.id === m.id ? { ...x, isActive } : x)));
    setBusy(m.id);
    try {
      await api.patch(`/admin/milestones/${m.id}`, { isActive });
      show(
        isActive ? `“${m.title}” is on again.` : `“${m.title}” is off. Nobody new earns it.`,
        'good',
        undoable ? { label: 'Undo', run: () => setActive(m, !isActive, false) } : undefined,
      );
    } catch (e) {
      qc.setQueryData(qk.milestones, before);
      show(errorMessage(e), 'bad');
    } finally {
      setBusy(null);
      void qc.invalidateQueries({ queryKey: qk.milestones });
    }
  };
  const toggle = (m: Milestone) => setActive(m, !m.isActive);
  const remove = async (m: Milestone) => {
    setBusy(m.id);
    try {
      const r = await api.del<{ deleted: boolean; deactivated: boolean }>(`/admin/milestones/${m.id}`);
      show(r.deleted ? `“${m.title}” removed.` : `“${m.title}” was already earned, so it is switched off instead of removed.`);
      refresh();
    } catch (e) {
      show(errorMessage(e), 'bad');
    } finally {
      setBusy(null);
    }
  };

  const who = (r: EarnedReward) =>
    r.audience === 'RIDER' ? (
      <Link to="/riders/$riderId" params={{ riderId: r.subjectId }} className="link" onClick={(e) => e.stopPropagation()}>
        Rider #{shortId(r.subjectId)}
      </Link>
    ) : (
      <Link to="/drivers/$driverId" params={{ driverId: r.subjectId }} className="link" onClick={(e) => e.stopPropagation()}>
        Driver #{shortId(r.subjectId)}
      </Link>
    );

  return (
    <div className="stack">
      <PageHead
        title="Rewards"
        sub="Milestones riders and drivers earn after completed rides."
        actions={
          isOwner ? (
            <button className="btn btn-dark" onClick={() => setEditing('new')}>
              Add milestone
            </button>
          ) : (
            <Pill>View only</Pill>
          )
        }
      />
      <Chips<RewardAudience>
        label="Who earns"
        value={audience}
        onChange={(v) => nav({ search: { audience: v === 'RIDER' ? undefined : v } })}
        options={[
          { value: 'RIDER', label: 'Riders' },
          { value: 'DRIVER', label: 'Drivers' },
        ]}
      />
      <FlashNote flash={flash} />
      {!isOwner && <Note>Only the owner can add or change milestones.</Note>}
      {mine.some((m) => m.isPlaceholder) && (
        <Note tone="warn">Milestones marked Placeholder are starting examples the owner hasn’t approved yet. They are live: people earn them.</Note>
      )}

      <section className="card" aria-label="Milestones">
        <div className="card-head">
          <h2>
            Milestones <span className="count-chip">{mine.length}</span>
          </h2>
          <span className="small muted">Counted in completed rides</span>
        </div>
        {milestones.isLoading ? (
          <Loading rows={3} />
        ) : milestones.error ? (
          <div className="card-body">
            <ErrorNote error={milestones.error} />
          </div>
        ) : !mine.length ? (
          <Empty title="No milestones yet" icon={IconGift}>
            {isOwner ? 'Add one: for example, ₹20 off the 5th ride.' : 'The owner hasn’t added any.'}
          </Empty>
        ) : (
          <ul className="rows" data-testid="milestones">
            {mine.map((m) => (
              <li key={m.id} data-testid={`milestone-${m.threshold}`}>
                <div className="row-link" style={{ cursor: 'default', flexWrap: 'wrap' }}>
                  <span className="veh-tile" style={{ width: 52, flexDirection: 'column', gap: 0, lineHeight: 1 }}>
                    <strong className="num" style={{ fontSize: 17 }}>
                      {m.threshold}
                    </strong>
                    <span style={{ fontSize: 10, fontWeight: 700 }}>{audience === 'RIDER' ? 'rides' : 'trips'}</span>
                  </span>
                  <span className="row-main">
                    <span className="row-title">
                      <span className="truncate">{m.title}</span>
                      {m.isPlaceholder && <Pill tone="warn">Placeholder</Pill>}
                    </span>
                    <span className="row-sub">
                      {rewardText(m.rewardKind, m.rewardPaise, m.rewardPercent, m.rewardMaxPaise)} · earned {m.earnedCount} time
                      {m.earnedCount === 1 ? '' : 's'}
                    </span>
                  </span>
                  <span className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
                    {m.isActive ? <Pill tone="good">On</Pill> : <Pill>Off</Pill>}
                    {isOwner && (
                      <>
                        <button className="btn btn-sm" onClick={() => setEditing(m)}>
                          Edit
                        </button>
                        <button className="btn btn-sm" disabled={busy === m.id} onClick={() => void toggle(m)}>
                          {m.isActive ? 'Turn off' : 'Turn on'}
                        </button>
                        {m.earnedCount === 0 && (
                          <button className="btn btn-sm btn-danger" disabled={busy === m.id} onClick={() => void remove(m)}>
                            Remove
                          </button>
                        )}
                      </>
                    )}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card" aria-label="Rewards earned">
        <div className="card-head">
          <h2>Earned</h2>
          <Chips<StatusFilter>
            label="Status"
            value={status}
            onChange={setStatus}
            options={[
              { value: 'ALL', label: 'All' },
              ...(audience === 'RIDER'
                ? (['AVAILABLE', 'APPLIED', 'USED', 'VOID'] as const).map((s) => ({ value: s, label: REWARD_STATUS_LABEL[s] }))
                : [{ value: 'CREDITED' as const, label: REWARD_STATUS_LABEL.CREDITED }]),
            ]}
          />
        </div>
        {earned.isLoading ? (
          <Loading rows={4} />
        ) : earned.error ? (
          <div className="card-body">
            <ErrorNote error={earned.error} />
          </div>
        ) : !earned.data?.items.length ? (
          <Empty title="Nothing earned yet" icon={IconGift}>
            Rewards appear here when someone reaches a milestone.
          </Empty>
        ) : (
          <>
            <DataTable
              label="Rewards earned"
              rows={earned.data.items}
              rowKey={(r) => r.id}
              columns={[
                { key: 'who', header: audience === 'RIDER' ? 'Rider' : 'Driver', cell: who },
                {
                  key: 'what',
                  header: 'Reward',
                  cell: (r) => (
                    <div>
                      <div className="primary">{r.title}</div>
                      <div className="secondary">{rewardText(r.kind, r.amountPaise, r.percent, r.maxPaise)}</div>
                    </div>
                  ),
                },
                { key: 'when', header: 'Earned', className: 'nowrap', cell: (r) => day(r.createdAt) },
                { key: 'status', header: 'Status', cell: (r) => <Pill tone={REWARD_STATUS_TONE[r.status]}>{REWARD_STATUS_LABEL[r.status]}</Pill> },
                {
                  key: 'act',
                  header: '',
                  className: 'right',
                  cell: (r) =>
                    isOwner && r.status === 'AVAILABLE' && r.audience === 'RIDER' ? (
                      <button className="btn btn-sm" onClick={() => setVoiding(r)}>
                        Withdraw
                      </button>
                    ) : null,
                },
              ]}
              card={(r) => (
                <>
                  <RowCard
                    title={r.title}
                    sub={
                      <>
                        {who(r)} · {day(r.createdAt)}
                      </>
                    }
                    side={<Pill tone={REWARD_STATUS_TONE[r.status]}>{REWARD_STATUS_LABEL[r.status]}</Pill>}
                  />
                </>
              )}
              phoneExtra={(r) =>
                isOwner && r.status === 'AVAILABLE' && r.audience === 'RIDER' ? (
                  <button className="btn btn-sm btn-block" onClick={() => setVoiding(r)}>
                    Withdraw this reward
                  </button>
                ) : null
              }
            />
            <Pager page={page} pageSize={earned.data.pageSize} total={earned.data.total} onPage={(p) => nav({ search: { ...search, page: p } })} />
          </>
        )}
      </section>
      <p className="small faint">
        Driver bonuses go onto the driver’s cash balance; rider rewards are taken off their next ride at booking.
      </p>

      {editing && (
        <Dialog title={editing === 'new' ? 'Add a milestone' : 'Change milestone'} onClose={() => setEditing(null)}>
          <MilestoneForm
            audience={audience}
            current={editing === 'new' ? null : editing}
            onDone={(text) => {
              show(text);
              refresh();
            }}
          />
        </Dialog>
      )}
      {voiding && (
        <ReasonDialog
          title="Withdraw this reward?"
          body={`${voiding.title} — the rider can’t use it after this.`}
          confirmLabel="Withdraw"
          danger
          onClose={() => setVoiding(null)}
          onConfirm={async (reason) => {
            await api.post(`/admin/rewards/${voiding.id}/void`, { reason });
            show('Reward withdrawn.');
            refresh();
          }}
        />
      )}
    </div>
  );
}

function MilestoneForm({ audience, current, onDone }: { audience: RewardAudience; current: Milestone | null; onDone: (text: string) => void }) {
  const close = useDialogClose();
  const kinds: RewardKind[] = audience === 'DRIVER' ? ['DRIVER_CASH_BONUS'] : ['RIDER_RIDE_CREDIT', 'RIDER_DISCOUNT_PERCENT'];
  const [kind, setKind] = useState<RewardKind>(current?.rewardKind ?? kinds[0]);
  const [threshold, setThreshold] = useState(String(current?.threshold ?? ''));
  const [title, setTitle] = useState(current?.title ?? '');
  const [amount, setAmount] = useState(current ? String(current.rewardPaise / 100) : '');
  const [pct, setPct] = useState(String(current?.rewardPercent ?? ''));
  const [max, setMax] = useState(current?.rewardMaxPaise ? String(current.rewardMaxPaise / 100) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const n = Number(threshold);
  const isPct = kind === 'RIDER_DISCOUNT_PERCENT';
  const problems = [
    !Number.isInteger(n) || n < 1 ? 'Rides needed: a whole number, 1 or more.' : null,
    title.trim().length < 2 ? 'Give it a short title riders or drivers will see.' : null,
    isPct ? (!(Number(pct) >= 1 && Number(pct) <= 100 && Number.isInteger(Number(pct))) ? 'Percent: a whole number from 1 to 100.' : null) : !(Number(amount) > 0) ? 'Amount: more than ₹0.' : null,
  ].filter(Boolean) as string[];

  const suggested = () => {
    const unit = audience === 'RIDER' ? 'rides' : 'trips';
    if (isPct) return `${threshold || 'N'} ${unit}: ${pct || 'N'}% off a ride${max ? ` (up to ₹${max})` : ''}`;
    return `${threshold || 'N'} ${unit}: ₹${amount || 'N'} ${kind === 'DRIVER_CASH_BONUS' ? 'bonus' : 'off a ride'}`;
  };

  return (
    <form
      className="stack-sm"
      style={{ gap: 16 }}
      onSubmit={async (e) => {
        e.preventDefault();
        if (problems.length) return;
        setBusy(true);
        setError(null);
        const body = {
          threshold: n,
          title: title.trim(),
          rewardKind: kind,
          ...(isPct
            ? { rewardPercent: Number(pct), ...(max ? { rewardMaxPaise: Math.round(Number(max) * 100) } : {}) }
            : { rewardPaise: Math.round(Number(amount) * 100) }),
        };
        try {
          if (current) await api.patch(`/admin/milestones/${current.id}`, body);
          else await api.post('/admin/milestones', { audience, ...body });
          onDone(current ? 'Milestone saved. It applies to rewards earned from now on.' : 'Milestone added. It is earned on the next completed ride.');
          close();
        } catch (err) {
          setError(errorMessage(err));
          setBusy(false);
        }
      }}
    >
      <p className="muted">{audience === 'RIDER' ? 'For riders.' : 'For drivers.'} Earned once per person, when they complete this many rides.</p>
      {kinds.length > 1 && (
        <Chips<RewardKind> label="Reward" value={kind} onChange={setKind} options={kinds.map((k) => ({ value: k, label: REWARD_KIND_LABEL[k] }))} />
      )}
      <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
        <label className="field">
          <span className="field-label">Rides needed</span>
          <input className="input num" inputMode="numeric" value={threshold} onChange={(e) => setThreshold(e.target.value.replace(/\D/g, '').slice(0, 6))} />
        </label>
        {isPct ? (
          <>
            <label className="field">
              <span className="field-label">Percent off</span>
              <span className="input-affix">
                <input className="input num has-suffix" inputMode="numeric" value={pct} onChange={(e) => setPct(e.target.value.replace(/\D/g, '').slice(0, 3))} />
                <span className="suffix">%</span>
              </span>
            </label>
            <label className="field">
              <span className="field-label">Up to (optional)</span>
              <span className="input-affix">
                <span className="prefix">₹</span>
                <input className="input num has-prefix" inputMode="decimal" value={max} onChange={(e) => setMax(e.target.value.replace(/[^\d.]/g, '').slice(0, 7))} />
              </span>
            </label>
          </>
        ) : (
          <label className="field">
            <span className="field-label">{kind === 'DRIVER_CASH_BONUS' ? 'Bonus' : 'Off a ride'}</span>
            <span className="input-affix">
              <span className="prefix">₹</span>
              <input className="input num has-prefix" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, '').slice(0, 7))} />
            </span>
          </label>
        )}
      </div>
      <label className="field">
        <span className="field-label">Title</span>
        <input className="input" maxLength={80} value={title} placeholder={suggested()} onChange={(e) => setTitle(e.target.value)} />
        <button type="button" className="btn btn-sm btn-ghost" style={{ alignSelf: 'flex-start' }} onClick={() => setTitle(suggested())}>
          Use “{suggested()}”
        </button>
      </label>
      {problems.length > 0 && (threshold || title || amount || pct) && <p className="field-hint bad">{problems[0]}</p>}
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      <div className="actions">
        <button type="button" className="btn" onClick={close}>
          Cancel
        </button>
        <button className="btn btn-dark" disabled={busy || problems.length > 0}>
          {busy ? 'Saving…' : current ? 'Save milestone' : 'Add milestone'}
        </button>
      </div>
    </form>
  );
}
